import { forwardRef, useImperativeHandle } from 'react';
import { z } from 'zod';
import { Stack } from '@optimus-web/core';
import { OrderItem as GeneratedOrderItem, useRepositoryMutation } from '@mews-commander/data-access';
import { SchemaFormDefinition, SchemaFormFields } from '@mews-commander/form';
import { setBills } from '@mews-commander/legacy-entities/src/ducks/bills';
import { deleteOrderItems, setOrderItems } from '@mews-commander/legacy-entities/src/ducks/orderItems';
import { OrderItem } from '@mews-commander/types';
import { LocalizedParagraph, LocalizedSubsectionTitle } from '@mews-commander/ui';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { createZodFormResolver, lt, useFormControls, Validation } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ExtendedOrderItemName } from '../components/ExtendedOrderItemName';
import { ItemTotalCost } from './ItemTotalCost';

enum CancelItemFields {
    Reason = 'reason',
}

const cancelItemSchema = z.object({
    [CancelItemFields.Reason]: Validation.String.Required.max(99),
});

export type CancelItemFormValues = z.infer<typeof cancelItemSchema>;

interface Props {
    closeDialog: () => void;
    orderItem: OrderItem;
    handleSuccessfulSubmit: () => void;
}

export const CancelBillItemDialogForm = forwardRef(({ closeDialog, orderItem, handleSuccessfulSubmit }: Props, ref) => {
    const dispatch = useCommanderThunkDispatch();

    const { mutateAsync } = useRepositoryMutation('orderItems', 'doublePostingCancel', {
        onSuccess: ({ orderItems, bills }) => {
            dispatch(deleteOrderItems([orderItem.id]));
            // @ts-expect-error
            dispatch(setOrderItems(orderItems));
            dispatch(setBills(bills));
        },
    });

    const { renderFormContent, submit } = useFormControls<CancelItemFormValues>({
        resolver: createZodFormResolver(cancelItemSchema),
        onSubmit: async ({ reason }) => {
            await mutateAsync({
                orderItemCancellations: [
                    {
                        orderItemId: orderItem.id,
                    },
                ],
                reason,
            });
            handleSuccessfulSubmit();
            closeDialog();
        },
    });

    const formDefinition: SchemaFormDefinition<typeof cancelItemSchema> = {
        fields: {
            reason: {
                placeholder: lt('EnterReason'),
                label: lt('ReasonForRemoval'),
                field: {
                    type: 'textarea',
                    rows: 1,
                },
            },
        },
    };

    useImperativeHandle(
        ref,
        () => ({
            submit,
        }),
        [submit]
    );

    return renderFormContent(
        <Stack vertical spacing="700">
            <Stack vertical spacing="300">
                <span>
                    <LocalizedParagraph textKey={TextKey.EditItemExplanation} />
                </span>
                <LocalizedSubsectionTitle textKey={TextKey.ItemsToBeRemoved} />
                <ExtendedOrderItemName orderItem={orderItem} />
                <SchemaFormFields schema={cancelItemSchema} formDefinition={formDefinition} />
            </Stack>
            <ItemTotalCost orderItem={orderItem as unknown as GeneratedOrderItem} />
        </Stack>
    );
});
